import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Calendar,
  dayjsLocalizer,
  type EventProps,
  type View,
} from "react-big-calendar"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  AgendaSlotDto,
  useAgendaControllerGetQuery,
  useAgendaControllerListQuery,
} from "~/ui-rtk/api/agendaApi"
import { type AuditView, useListAuditsQuery } from "~/ui-rtk/api/auditsApi"
import {
  KeyEventDto,
  useKeyEventControllerGetAllKeyEventsQuery,
} from "~/ui-rtk/api/keyEventsApi"
import { Loader } from "~/ui-rtk/components/ui/common/Loader"
import CheckboxWithLabel from "~/ui-rtk/components/ui/controls/CheckboxWithLabel"
import useDate from "~/ui-rtk/hooks/date"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { auditTypeMeta, SLOT_STATUS_META, SLOT_TYPE_META } from "./constants"
import AssigneeFilter, { UNASSIGNED_FILTER } from "./components/AssigneeFilter"
import CalendarToolbar from "./components/CalendarToolbar"
import KeyEventDialog from "./components/KeyEventDialog"
import ReportDialog from "./components/ReportDialog"
import SlotDialog from "./components/SlotDialog"
import StatusIcon from "./components/StatusIcon"
import AuditDialog from "./components/AuditDialog"
import { useAgendaActions } from "./hooks/useAgendaActions"
import { useAgendaRange } from "./hooks/useAgendaRange"
import { useAssignableUsers } from "./hooks/useAssignableUsers"
import { type AuditItem, initials, itemName, slotProgress } from "./utils"

import "react-big-calendar/lib/css/react-big-calendar.css"
import "./calendar.css"

// The date hook module (imported above) registers all dayjs plugins/locale
// before the localizer is created.
const localizer = dayjsLocalizer(dayjs)

const VIEWS: View[] = ["month", "week", "day", "agenda"]

/**
 * A calendar entry: an agenda slot, a company key event, or a recorded action
 * from the audit history (the latter two are read-only overlays).
 */
type CalendarEntry = {
  id: string
  title: string
  start: Date
  end: Date
  allDay: boolean
  resource:
    | { kind: "slot"; slot: AgendaSlotDto; assigneeInitials?: string }
    | { kind: "keyEvent"; keyEvent: KeyEventDto }
    | { kind: "audit"; audit: AuditView }
}

/**
 * Type is signalled by a colored LEFT ACCENT plus a soft (~15%) tint of the
 * same color — enough to color-code at a glance while keeping white text and
 * the colored status icons readable (a saturated full background wasn't).
 */
const EVENT_TYPE_CLASS: Record<AgendaSlotDto["type"], string> = {
  AD_BLAST: "!border-l-primary !bg-primary/15",
  AD_CREATIVE: "!border-l-secondary !bg-secondary/15",
  BUDGET_RECOMMENDATION: "!border-l-accent !bg-accent/15",
  GENERIC: "!border-l-system-gray-500 !bg-system-gray-500/15",
}

function EntryChip({ event }: EventProps<CalendarEntry>) {
  if (event.resource.kind === "keyEvent") {
    return (
      <span className="flex w-full items-center gap-1.5 text-xs font-medium">
        <span className="shrink-0">★</span>
        <span className="min-w-0 flex-1 truncate">{event.title}</span>
      </span>
    )
  }
  if (event.resource.kind === "audit") {
    const { audit } = event.resource
    return (
      <span className="flex w-full items-center gap-1.5 text-xs font-medium">
        <span className="shrink-0 tabular-nums text-[10px] opacity-70">
          {dayjs(audit.createdAt).format("HH:mm")}
        </span>
        <span className="min-w-0 flex-1 truncate">{event.title}</span>
        {audit.failed > 0 && (
          <span className="shrink-0 rounded bg-red-500/30 px-1 text-[10px] leading-4 text-red-200">
            {audit.failed}✕
          </span>
        )}
      </span>
    )
  }
  const { slot, assigneeInitials } = event.resource
  const progress = slotProgress(slot)
  return (
    <span className="flex w-full items-center gap-1.5 text-xs font-medium">
      <StatusIcon status={slot.status} className="shrink-0" />
      {progress ? (
        <span className="shrink-0 rounded bg-white/20 px-1 text-[10px] leading-4">
          {progress.completed}/{progress.target}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{event.title}</span>
      {assigneeInitials && (
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-white/25 text-[9px] font-bold leading-none">
          {assigneeInitials}
        </span>
      )}
    </span>
  )
}

export default function BrandAgendaPage() {
  const dayjsHook = useDate()
  const [view, setView] = useState<View>("month")
  const [date, setDate] = useState<Date>(() => new Date())

  const [dialogOpen, setDialogOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [editingSlot, setEditingSlot] = useState<AgendaSlotDto | null>(null)
  const [createDate, setCreateDate] = useState(dayjsHook().format("YYYY-MM-DD"))

  const [searchParams, setSearchParams] = useSearchParams()

  const range = useAgendaRange(view, date)
  const { runSlotAction } = useAgendaActions()

  const {
    data: slots = [],
    isLoading,
    isError,
    refetch,
  } = useAgendaControllerListQuery({
    from: range.from,
    to: range.to,
  })

  const { users } = useAssignableUsers()
  const assigneeNames = useMemo(
    () => new Map(users.map(u => [u.id, u.name])),
    [users],
  )

  // Company key events (product launches, promos…) overlay the calendar as
  // read-only entries so slot planning happens around them. The list endpoint
  // has no date filter — filter to the visible window client-side.
  const { data: keyEvents = [] } = useKeyEventControllerGetAllKeyEventsQuery()
  const [viewingKeyEvent, setViewingKeyEvent] = useState<KeyEventDto | null>(
    null,
  )

  // Recorded actions (audits) overlay the calendar read-only so planned work
  // and what actually happened live on one view. Toggleable — busy accounts
  // can rack up dozens of status/budget rows a day.
  const [showHistory, setShowHistory] = useState(true)
  const [viewingAudit, setViewingAudit] = useState<AuditView | null>(null)
  const { data: audits = [] } = useListAuditsQuery(
    { from: range.from, to: range.to, take: 200 },
    { skip: !showHistory },
  )

  // Linear-style per-person filter (multi-select; empty set = everyone).
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set())
  const toggleAssigneeFilter = (id: string) =>
    setAssigneeFilter(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const filteredSlots = useMemo(() => {
    if (assigneeFilter.size === 0) return slots
    return slots.filter(slot =>
      assigneeFilter.has(slot.assigneeId ?? UNASSIGNED_FILTER),
    )
  }, [slots, assigneeFilter])

  const events = useMemo<CalendarEntry[]>(() => {
    const slotEntries: CalendarEntry[] = filteredSlots.map(slot => ({
      id: slot.id,
      title: slot.title,
      start: dayjsHook(slot.scheduledDate).startOf("day").toDate(),
      end: dayjsHook(slot.endDate ?? slot.scheduledDate)
        .endOf("day")
        .toDate(),
      allDay: true,
      resource: {
        kind: "slot",
        slot,
        assigneeInitials: slot.assigneeId
          ? initials(assigneeNames.get(slot.assigneeId))
          : undefined,
      },
    }))
    const from = dayjsHook(range.from)
    const to = dayjsHook(range.to)
    const keyEventEntries: CalendarEntry[] = keyEvents
      .filter(keyEvent => {
        const start = dayjsHook(keyEvent.start)
        const end = dayjsHook(keyEvent.end ?? keyEvent.start)
        return start.isBefore(to) && !end.isBefore(from)
      })
      .map(keyEvent => ({
        id: `key-event-${keyEvent.id}`,
        title: keyEvent.name,
        start: dayjsHook(keyEvent.start).startOf("day").toDate(),
        end: dayjsHook(keyEvent.end ?? keyEvent.start)
          .endOf("day")
          .toDate(),
        allDay: true,
        resource: { kind: "keyEvent", keyEvent },
      }))
    const auditEntries: CalendarEntry[] = audits.map(audit => {
      const start = dayjsHook(audit.createdAt)
      const label = auditTypeMeta(audit.type).label
      const items = audit.items as AuditItem[]
      const single =
        audit.total === 1 && items?.[0] ? itemName(items[0], 0) : null
      return {
        id: `audit-${audit.id}`,
        title: single
          ? `${label} · ${single}`
          : `${label} · ${audit.total} items`,
        start: start.toDate(),
        // Zero-length events collapse in the week/day time grid — give each
        // action a nominal half-hour block.
        end: start.add(30, "minute").toDate(),
        allDay: false,
        resource: { kind: "audit", audit },
      }
    })
    // Key events first so they render above the day's slots.
    return [...keyEventEntries, ...slotEntries, ...auditEntries]
  }, [
    filteredSlots,
    assigneeNames,
    keyEvents,
    audits,
    range.from,
    range.to,
    dayjsHook,
  ])

  const eventPropGetter = useCallback((event: CalendarEntry) => {
    if (event.resource.kind === "keyEvent") {
      return {
        className:
          "!rounded-md !border-0 !border-l-4 !border-solid !border-l-amber-400 !bg-amber-500/15 !text-amber-100",
      }
    }
    if (event.resource.kind === "audit") {
      return {
        className: cn(
          "!rounded-md !border-0 !border-l-4 !border-solid !text-white",
          auditTypeMeta(event.resource.audit.type).accentClass,
        ),
      }
    }
    const { slot } = event.resource
    return {
      className: cn(
        "!rounded-md !border-0 !border-l-4 !border-solid !text-white",
        EVENT_TYPE_CLASS[slot.type],
        (slot.status === "DONE" || slot.status === "CANCELLED") && "opacity-60",
      ),
    }
  }, [])

  const tooltipAccessor = useCallback(
    (event: CalendarEntry) => {
      if (event.resource.kind === "keyEvent") {
        const { keyEvent } = event.resource
        return `${keyEvent.name} · ${keyEvent.type} · key event`
      }
      if (event.resource.kind === "audit") {
        const { audit } = event.resource
        const outcome =
          audit.failed > 0
            ? `${audit.successful} ok, ${audit.failed} failed`
            : `${audit.successful} ok`
        return `${auditTypeMeta(audit.type).label} · ${audit.total} item${audit.total === 1 ? "" : "s"} · ${outcome}`
      }
      const { slot } = event.resource
      const assignee = slot.assigneeId
        ? (assigneeNames.get(slot.assigneeId) ?? "Assigned")
        : "Unassigned"
      return `${slot.title} · ${SLOT_TYPE_META[slot.type].label} · ${SLOT_STATUS_META[slot.status].label} · ${assignee}`
    },
    [assigneeNames],
  )

  const openCreate = (dayISO: string) => {
    setEditingSlot(null)
    setCreateDate(dayISO)
    setDialogOpen(true)
  }
  const openEdit = (slot: AgendaSlotDto) => {
    setEditingSlot(slot)
    setDialogOpen(true)
  }
  const closeDialog = () => {
    setDialogOpen(false)
    setEditingSlot(null)
    if (searchParams.has("slot")) {
      searchParams.delete("slot")
      setSearchParams(searchParams, { replace: true })
    }
  }

  // Deep link from notifications: /brand-agenda?slot=<id>. The slot may live
  // outside the currently visible window (link opened weeks later, or a
  // future-month slot), so when the list doesn't contain it fall back to a
  // single-slot fetch, jump the calendar to its date, and open the dialog.
  const deepLinkSlotId = searchParams.get("slot")
  const deepLinkInList = deepLinkSlotId
    ? slots.some(s => s.id === deepLinkSlotId)
    : false
  const { data: fetchedDeepLinkSlot, isError: isDeepLinkError } =
    useAgendaControllerGetQuery(
      { id: deepLinkSlotId ?? "" },
      { skip: !deepLinkSlotId || deepLinkInList || dialogOpen },
    )
  useEffect(() => {
    if (!deepLinkSlotId || dialogOpen) return
    const match =
      slots.find(s => s.id === deepLinkSlotId) ??
      (fetchedDeepLinkSlot?.id === deepLinkSlotId
        ? fetchedDeepLinkSlot
        : undefined)
    if (match) {
      setDate(new Date(match.scheduledDate))
      setEditingSlot(match)
      setDialogOpen(true)
    }
  }, [deepLinkSlotId, slots, fetchedDeepLinkSlot, dialogOpen])

  // Deleted (or inaccessible) slot: say so and drop the param instead of
  // silently doing nothing.
  useEffect(() => {
    if (!deepLinkSlotId || !isDeepLinkError) return
    toast.error("That agenda slot no longer exists")
    searchParams.delete("slot")
    setSearchParams(searchParams, { replace: true })
  }, [deepLinkSlotId, isDeepLinkError, searchParams, setSearchParams])

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Brand Agenda</h1>
          <p className="text-sm text-basic-blue">
            Plan and assign the work scheduled for this brand.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={() => setReportOpen(true)}
        >
          Action report
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <AssigneeFilter
          slots={slots}
          users={users}
          selected={assigneeFilter}
          onToggle={toggleAssigneeFilter}
          onClear={() => setAssigneeFilter(new Set())}
        />
        <CheckboxWithLabel
          checked={showHistory}
          onChange={event => setShowHistory(event.target.checked)}
          className="checkbox checkbox-sm"
        >
          <span className="text-xs text-system-gray-400">
            Show action history
          </span>
        </CheckboxWithLabel>
      </div>

      {isError && (
        <div className="alert alert-error">
          <span>Failed to load agenda slots.</span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <Calendar<CalendarEntry>
          className="brand-agenda-calendar"
          localizer={localizer}
          events={events}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={VIEWS}
          popup
          selectable
          longPressThreshold={250}
          onSelectSlot={info =>
            openCreate(dayjsHook(info.start).format("YYYY-MM-DD"))
          }
          onSelectEvent={event => {
            if (event.resource.kind === "keyEvent") {
              setViewingKeyEvent(event.resource.keyEvent)
            } else if (event.resource.kind === "audit") {
              setViewingAudit(event.resource.audit)
            } else {
              openEdit(event.resource.slot)
            }
          }}
          eventPropGetter={eventPropGetter}
          tooltipAccessor={tooltipAccessor}
          components={{ toolbar: CalendarToolbar, event: EntryChip }}
        />
      )}

      <SlotDialog
        isOpen={dialogOpen}
        // Prefer the freshly-fetched copy so link approvals / recomputed
        // status show up in the open dialog after each mutation refetch.
        slot={
          editingSlot
            ? (slots.find(s => s.id === editingSlot.id) ?? editingSlot)
            : null
        }
        defaultDate={createDate}
        onClose={closeDialog}
        onAction={runSlotAction}
      />

      <ReportDialog isOpen={reportOpen} onClose={() => setReportOpen(false)} />

      <KeyEventDialog
        keyEvent={viewingKeyEvent}
        onClose={() => setViewingKeyEvent(null)}
      />

      <AuditDialog audit={viewingAudit} onClose={() => setViewingAudit(null)} />
    </div>
  )
}
